import ApiService from "./controller";
import type { SimpleMessage, EvaluationDataType, EvaluationSource, EvaluationDataResponse } from "./models";

// ==================== Evaluation API Response Types ====================

export interface EvaluationDataPaginatedResponse {
    data: EvaluationDataResponse[];
    total: number;
}

export interface EvaluationRunResponse {
    id: string;
    name: string;
    status: string;
    created_at: string;
    started_at?: string;
    completed_at?: string;
    synthetic_question_count: number;
    ground_truth_question_count: number;
    duration_seconds?: number;
    num_tests_per_question?: number;
}

export interface EvaluationRunPaginatedResponse {
    data: EvaluationRunResponse[];
    total: number;
}

export interface EvaluationSettingOption {
    id: string;
    name: string;
    category: string;
}

// Paginated response helpers
function buildPaginatedUrl(
    basePath: string,
    limit: number,
    offset: number,
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[]; quickFilterValues?: string[] },
): string {
    let url = `${basePath}?limit=${limit}&offset=${offset}`;
    if (sortField && sortOrder) {
        url += `&sort_field=${sortField}&sort_order=${sortOrder}`;
    }
    if (filterModel?.quickFilterValues && filterModel.quickFilterValues.length > 0) {
        url += `&quick_filter=${encodeURIComponent(filterModel.quickFilterValues[0])}`;
    }
    const filters: any[] = [...(filterModel?.items || [])];
    if (filters.length > 0) {
        url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
    }
    return url;
}

// ==================== Evaluation Data API ====================

export async function fetchEvaluationDataPaginated(
    limit: number = 50,
    offset: number = 0,
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[]; quickFilterValues?: string[] },
    dataType?: string,
): Promise<EvaluationDataPaginatedResponse> {
    let url = buildPaginatedUrl(
        "/api/v2/evaluation/data",
        limit,
        offset,
        sortField,
        sortOrder,
        filterModel,
    );
    if (dataType) {
        url += `&data_type=${encodeURIComponent(dataType)}`;
    }
    return ApiService.get<EvaluationDataPaginatedResponse>(url);
}

export async function createEvaluationData(data: {
    question: string;
    answer: string;
    data_type: EvaluationDataType;
    sources?: any[];
}): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>("/api/v2/evaluation/data", data);
}

export async function updateEvaluationData(
    id: string,
    data: Partial<{
        question: string;
        expected_answer: string;
        topic: string;
        question_target: string;
        data_type: EvaluationDataType;
        sources: any[];
    }>,
): Promise<SimpleMessage> {
    return ApiService.put<SimpleMessage>(`/api/v2/evaluation/data/${id}`, data);
}

export async function deleteEvaluationData(dataId: string): Promise<SimpleMessage> {
    return ApiService.delete<SimpleMessage>(`/api/v2/evaluation/data/${dataId}`);
}

export async function bulkDeleteEvaluationData(ids: string[]): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>("/api/v2/evaluation/data/bulk-delete", ids);
}

export async function deleteAllEvaluationDataByType(
    dataType: EvaluationDataType,
): Promise<SimpleMessage> {
    return ApiService.delete<SimpleMessage>(`/api/v2/evaluation/data/by-type/${dataType}`);
}

export async function syncEvaluationDataSources(dataId: string): Promise<{
    sources: Array<{ type: string; id: string; name?: string; pages?: number[]; linked: boolean }>;
}> {
    return ApiService.post(`/api/v2/evaluation/data/${dataId}/sync-sources`, {});
}

export async function syncAllEvaluationSources(): Promise<{
    records_processed: number;
    records_updated: number;
    newly_linked: number;
    still_unresolved: number;
}> {
    return ApiService.post("/api/v2/evaluation/data/sync-all-sources", {});
}

export async function generateSyntheticData(data: {
    count: number;
    ensure_coverage?: boolean;
    faq_percentage?: number;
    document_percentage?: number;
}): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>("/api/v2/evaluation/data/generate-synthetic", data);
}

export async function importEvaluationData(
    file: File,
): Promise<{ created: number; total: number; errors: string[]; source_warnings: string[] }> {
    const formData = new FormData();
    formData.append("file", file);
    return ApiService.postForm<{ created: number; total: number; errors: string[]; source_warnings: string[] }>("/api/v2/evaluation/data/ground-truth", formData);
}

// ==================== Evaluation Runs API ====================

export async function fetchEvaluationRunsPaginated(
    limit: number = 50,
    offset: number = 0,
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[]; quickFilterValues?: string[] },
): Promise<EvaluationRunPaginatedResponse> {
    let url = `/api/v2/evaluation/runs?limit=${limit}&offset=${offset}`;
    if (sortField && sortOrder) {
        url += `&sort_field=${sortField}&sort_order=${sortOrder}`;
    }
    if (filterModel?.items && filterModel.items.length > 0) {
        url += `&filters=${encodeURIComponent(JSON.stringify(filterModel.items))}`;
    }
    return ApiService.get<EvaluationRunPaginatedResponse>(url);
}

export async function getEvaluationRun(runId: string): Promise<any> {
    return ApiService.get<any>(`/api/v2/evaluation/runs/${runId}`);
}

export async function createEvaluationRun(data: {
    name: string;
    synthetic_question_ids?: string[];
    ground_truth_question_ids?: string[];
    num_tests_per_question?: number;
}): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>("/api/v2/evaluation/runs", data);
}

export async function updateEvaluationRun(
    runId: string,
    data: Partial<{
        name: string;
        status: string;
    }>,
): Promise<SimpleMessage> {
    return ApiService.put<SimpleMessage>(`/api/v2/evaluation/runs/${runId}`, data);
}

export async function restartEvaluationRun(runId: string): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>(`/api/v2/evaluation/runs/${runId}/restart`, {});
}

export async function deleteEvaluationRun(runId: string): Promise<SimpleMessage> {
    return ApiService.delete<SimpleMessage>(`/api/v2/evaluation/runs/${runId}`);
}

export async function bulkDeleteEvaluationRuns(runIds: string[]): Promise<SimpleMessage> {
    return ApiService.delete<SimpleMessage>(`/api/v2/evaluation/runs/bulk-delete`, { ids: runIds });
}

// ==================== Evaluation Stats & Analysis ====================

export async function getEvaluationDataStats(): Promise<{
    total_questions: number;
    total_synthetic: number;
    total_ground_truth: number;
    avg_score: number;
}> {
    return ApiService.get<any>("/api/v2/evaluation/data/stats");
}

export async function calculateSelectedCoverage(questionIds: string[], dataType: string = "SYNTHETIC"): Promise<any> {
    return ApiService.post<any>("/api/v2/evaluation/data/coverage", {
        question_ids: questionIds,
        data_type: dataType,
    });
}

export async function calculateCoverageBreakdown(questionIds: string[], dataType: string = "SYNTHETIC"): Promise<any> {
    return ApiService.post<any>("/api/v2/evaluation/data/coverage-breakdown", {
        question_ids: questionIds,
        data_type: dataType,
    });
}

export async function calculateOverallCoverage(questionIds: string[]): Promise<any> {
    return ApiService.post<any>("/api/v2/evaluation/data/overall-coverage", {
        question_ids: questionIds,
    });
}

export async function calculateCoverageByType(questionIds: string[]): Promise<any> {
    return ApiService.post<any>("/api/v2/evaluation/data/coverage-by-type", {
        question_ids: questionIds,
    });
}

// ==================== Evaluation Settings ====================

export async function getEvaluationTopics(): Promise<{
    topics: string[];
}> {
    return ApiService.get<any>("/api/v2/evaluation/settings/topics");
}

export async function getEvaluationTargets(): Promise<{
    options: Array<{
        id: string;
        name: string;
        category: string;
    }>;
}> {
    return ApiService.get<any>("/api/v2/evaluation/settings/options?category=target");
}

// ==================== Evaluated Questions ====================

export async function getEvaluatedQuestions(
    runId: string,
    limit: number = 50,
    offset: number = 0,
    dataType?: string,
): Promise<any> {
    let url = `/api/v2/evaluation/runs/${runId}/evaluated-questions?limit=${limit}&offset=${offset}`;
    if (dataType) {
        url += `&data_type=${encodeURIComponent(dataType)}`;
    }
    return ApiService.get<any>(url);
}

export async function getRunProgress(runId: string): Promise<any> {
    return ApiService.get<any>(`/api/v2/evaluation/runs/${runId}/progress`);
}
